import { Component, OnInit, ViewChild, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { CaseManagementService } from '../case-management.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { MatDialog } from '@angular/material/dialog';
import { AdHocModalComponent } from '../ad-hoc-modal/ad-hoc-modal.component';
import { AdminService } from '../../admin/admin.service';
import { WarnDialogComponent } from '../../warn-dialog/warn-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RbGridComponent } from '../../shared-components/rb-grid/rb-grid.component';
import { UtilityAppsService } from '../../shared-components/utility-apps/utility-apps.service';

import { BreadcrumbComponent } from '../../breadcrumb/breadcrumb.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-case-management',
  templateUrl: './case-management.component.html',
  styleUrls: ['./case-management.component.scss'],
  imports: [
    RbGridComponent,
    BreadcrumbComponent,
    MatIconModule,
    MatButtonModule,
  ],
})
export class CaseManagementComponent implements OnInit, OnDestroy {
  private cms = inject(CaseManagementService);
  private ss = inject(SessionStorageService);
  fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private adminService = inject(AdminService);
  private route = inject(Router);
  private snacky = inject(MatSnackBar);
  private uas = inject(UtilityAppsService);

  @ViewChild(RbGridComponent, { static: false }) rbGrid: RbGridComponent;

  globals = this.ss.get('globals');
  rights: string[];
  crumbConfig = [
    {
      label: 'Case Management',
      link: 'case-management',
    },
  ];
  gridConfig = {
    title: 'Case Management',
    gridName: 'CaseManagement',
    showGridFilters: true,
    showFilterManager: true,
    manualSearch: true,
    searchButtonText: 'Search Results',
    downloadGridButton: true,
    downloadGridButtonProps: {
      icon: 'cloud_download',
      text: 'Download Case Management Data',
      action: 'application/xlsx',
      payloadOverride: { take: 1000, skip: 0, search: {}, sort: {} },
    },
    gridActionColumn: true,
    showGridColumnFilters: true,
    previewActionText: 'Review Case',
    rowSelector: 'CRID',
    detailViewRoute: '/case-management/case-manager/',
  };

  ngOnInit(): void {
    this.rights = this.ss.get('rights');

    this.uas.appsList.set([
      {
        label: 'Dashboard',
        link: '/dashboard',
        right: 'NewDashboardTab',
        icon: 'dashboard',
      },
      {
        label: 'Book Management',
        link: '/book-management',
        right: 'BookManagement',
        icon: 'menu_book',
      },
      {
        label: 'Post Transaction',
        link: '/post-trade',
        right: 'PostTrade',
        icon: 'history_edu',
      },
    ]);

    this.gridConfig['deleteRow'] = this.rights?.includes('DeleteCRID');
    this.gridConfig['deleteRowText'] = 'Delete Case';
    this.gridConfig['groupByOptions'] = this.rights?.includes('CMProductView')
      ? [
          { display: 'Case', value: 'GROUP' },
          { display: 'Product/Category', value: 'FLAT' },
        ]
      : null;
    this.gridConfig['resetBtnText'] = 'Clear Filters';
    this.getGridSetup();
  }

  ngOnDestroy(): void {
    this.uas.appsList.set([]);
  }

  getGridSetup(configParam = 'cm-grid') {
    return this.adminService.getUIConfigHeaders({
      uiconfig: 'cm-grid',
      type: configParam,
      implementation: 'cm',
    });
  }

  getGridData(reqData, responseType = 'application/json') {
    return this.cms.getCaseManagementData(reqData, responseType);
  }

  createAdHocCase() {
    const dialogRef = this.dialog.open(AdHocModalComponent, {
      panelClass: 'app-ad-hoc-modal',
      data: {
        typeFilters: this.rbGrid.externalFilters.filter(
          filter => filter.varName === 'Type'
        )[0].options,
      },
      width: '50vw',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.CRID) {
        this.route.navigate([`/case-management/case-manager/${result.CRID}`]);
      }
    });
  }

  deleteCase(dataItem) {
    let dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Attention!',
        content: `Are you sure you want to delete this case? This cannot be undone.`,
        confirm: 'Confirm',
        hideCancel: false,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      dialogRef = null;
      if (result === 'continue') {
        this.cms.deleteCase(dataItem.CRID.toString()).subscribe({
          next: res => {
            if (res && res['status'] === 'Success') {
              this.rbGrid.getGridSetup();
              this.snacky.open(
                `${dataItem.CRID.toString()} was deleted.`,
                'Close',
                {
                  duration: 4000,
                }
              );
            } else {
              this.snacky.open(
                `There was an error. ${dataItem.CRID.toString()} could not be updated.`,
                'Close',
                {
                  duration: 4000,
                }
              );
            }
          },
          error: error => console.error(error),
        });
      }
    });
  }
}
